import { useState, useEffect, useCallback } from 'react';
import { wordsOfWisdomAPI } from './services/api';
import './USA/GmailWordsOfWisdom.css';

// Branding assets — verified accessible URLs
const WOW_BANNER_URL = 'https://yugalkunj.org/images/wordofwisdom.png';

// Hardcoded fallback — shown when API is unreachable or returns no data
const FALLBACK_ENTRY = [
    {
        wordsOfWisdomId: 0,
        subject: 'God Loves You',
        senderName: 'Didi Ji',
        senderEmail: 's_didi@radhamadhavsociety.org',
        emailDate: '2026-08-28T00:00:00',
        snippet: 'Love is a word we have borrowed from the divine realm. God loves us. He says in the scripture Ramayan, "I lovingly take care of you like a mother takes care of her newborn."',
        htmlBody: `<p>Love is a word we have borrowed from the divine realm. God loves us. He says in the scripture Ramayan, "I lovingly take care of you like a mother takes care of her newborn." Even though the mother's love is the closest that comes to true love, yet it's not completely selfless. God's love is.</p>
<p>He shows His love for you in so many ways. Take some time to reflect on this fact. Who created your body within the body of your mother? It was God! Who provided nourishment when you were hanging upside down in the mother's womb? God! Who created milk within the mother's body so that you wouldn't starve to death? God! Who created grains, lentils, vegetables and fruits for your consumption? Again; it was God.</p>
<p>While loving His countless children simultaneously, He loves you individually. He sits in your heart, taking note of all your thoughts. Without your asking Him to do it, He provides fruits of all your actions. He rewards you for your goodness and punishes you for breaking His laws. Even His punishment is an act of love towards you. He is not a stern God; He is a loving and merciful God. His mercy flows continuously for you. His stock of forgiveness never ends for you.</p>
<p>There is not a single moment that goes by where He is not gazing lovingly at you. He doesn't even blink His eyes. He has waited an eternity to rush into His outstretched arms, and He will continue to wait. Even when you forget all about Him, He doesn't forget you. That's how much He loves you.</p>
<p>Love Him like a Lord and Master. Love Him like your Mother and Father. Love Him like your Best Friend. Love Him like your Child. Love Him like your most precious Beloved. Love Him so much that your eyes long to see Him, and your heart yearns to be with Him.</p>`,
        textBody: null,
    }
];
const FALLBACK_BANNER_URL = 'https://yugalkunj.org/images/wordofwisdom.png';
const FACEBOOK_URL = 'https://www.facebook.com/yugalkunj';
const YOUTUBE_URL = 'https://www.youtube.com/yugalkunj';
const INSTAGRAM_URL = 'https://www.instagram.com/yugalkunj';
const CONTACT_EMAIL = 's_didi@radhamadhavsociety.org';

const WordofWisdomeCard = () => {
    const [emails, setEmails] = useState([]);
    const [selectedEmail, setSelectedEmail] = useState(null);
    const [loading, setLoading] = useState(true);
    const [loadingDetail, setLoadingDetail] = useState(false);
    const [error, setError] = useState(null);

    // Fetch all Words of Wisdom emails
    const loadEmails = useCallback(async () => {
        try {
            setLoading(true);
            const result = await wordsOfWisdomAPI.getAll();
            if (result.success !== false) {
                const data = Array.isArray(result) ? result : result.data || [];
                setEmails(data.length > 0 ? data : FALLBACK_ENTRY);
            } else {
                setEmails(FALLBACK_ENTRY);
            }
        } catch (err) {
            // Network/connection errors — show hardcoded fallback content instead
            setEmails(FALLBACK_ENTRY);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadEmails();
    }, [loadEmails]);

    // Load detail of a single email
    const loadEmailDetail = useCallback(async (id) => {
        // Fallback entry (id 0) — use local data directly, no API call
        if (id === 0) {
            setSelectedEmail(FALLBACK_ENTRY[0]);
            return;
        }
        try {
            setLoadingDetail(true);
            const result = await wordsOfWisdomAPI.getById(id);
            const emailData = Array.isArray(result) ? result[0] : (result.data || result);
            if (emailData) {
                setSelectedEmail(emailData);
            } else {
                setError('Email not found');
            }
        } catch (err) {
            setError(err.message);
        } finally {
            setLoadingDetail(false);
        }
    }, []);

    // Handle image load errors by switching to a known fallback
    const handleImageError = (e) => {
        if (e.target.src !== FALLBACK_BANNER_URL) {
            e.target.src = FALLBACK_BANNER_URL;
        }
    };

    // Format date for display (long form)
    const formatDate = (dateStr) => {
        try {
            const d = new Date(dateStr);
            return d.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
        } catch {
            return dateStr;
        }
    };

    // Format date to match email template exactly (Aug. 7, 2026)
    const formatEmailDate = (dateStr) => {
        try {
            const d = new Date(dateStr);
            const month = d.toLocaleDateString('en-US', { month: 'short' });
            const day = d.getDate();
            const year = d.getFullYear();
            return `${month} ${day}, ${year}`;
        } catch {
            return dateStr;
        }
    };

    // Sanitize and prepare body content for the email-style template
    const getBodyContent = (email) => {
        if (email?.htmlBody && email.htmlBody.trim().length > 0) {
            return email.htmlBody;
        }
        if (email?.textBody && email.textBody.trim().length > 0) {
            return email.textBody.replace(/\n/g, '<br/>');
        }
        return '<p>No content available.</p>';
    };

    return (
        <div className="wow-public-page">
            <div className="wow-public-container">

                {/* Error Message */}
                {error && (
                    <div className="wow-error">
                        <i className="fas fa-exclamation-circle"></i>
                        <span>{error}</span>
                        <button onClick={() => setError(null)} aria-label="Dismiss">&#10005;</button>
                    </div>
                )}

                {/* Loading state */}
                {loading && (
                    <div className="wow-loading">
                        <div className="wow-loading__spinner"></div>
                        <p>Loading Words of Wisdom...</p>
                    </div>
                )}

                {/* Loading detail spinner */}
                {loadingDetail && (
                    <div className="wow-loading">
                        <div className="wow-loading__spinner"></div>
                        <p>Loading message...</p>
                    </div>
                )}

                {/* Detail view — full email template */}
                {selectedEmail && !loadingDetail && (
                    <div className="wow-email-card">
                        <button
                            className="wow-back-btn"
                            onClick={() => setSelectedEmail(null)}
                            aria-label="Back to messages"
                        >
                            <i className="fas fa-arrow-left"></i> Back to Messages
                        </button>

                        {/* Email header */}
                        <div className="wow-email-header">
                            <div className="wow-email-header__brand">
                                <div className="wow-email-header__text">
                                    <div className="wow-email-header__org">Radha Madhav Society</div>
                                    <div className="wow-email-header__date">{formatEmailDate(selectedEmail.emailDate)}</div>
                                </div>
                            </div>
                        </div>

                        {/* Email hero banner */}
                        <div className="wow-email-hero">
                            <img
                                src={WOW_BANNER_URL}
                                alt="Words of Wisdom"
                                className="wow-email-hero__image"
                                onError={handleImageError}
                            />
                        </div>

                        {/* Subject bar */}
                        <div className="wow-email-subject-bar">
                            <span>{selectedEmail.subject}</span>
                        </div>

                        {/* Body */}
                        <div className="wow-email-body">
                            <div
                                className="wow-email-content"
                                dangerouslySetInnerHTML={{ __html: getBodyContent(selectedEmail) }}
                            />

                            <div className="wow-email-signoff">
                                <p>Yours,</p>
                                <p className="wow-email-signoff__name">Didi Ji</p>
                            </div>

                            <hr className="wow-email-divider" />

                            <div className="wow-email-contact">
                                <p>
                                    <em>Note: You may direct your spiritual questions to me directly at</em>
                                </p>
                                <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>
                            </div>

                            <div className="wow-email-social">
                                <p>Follow us on</p>
                                <div className="wow-email-social__links">
                                    <a href={FACEBOOK_URL} target="_blank" rel="noopener noreferrer" aria-label="Facebook">
                                        <i className="fab fa-facebook-f"></i>
                                    </a>
                                    <a href={YOUTUBE_URL} target="_blank" rel="noopener noreferrer" aria-label="YouTube">
                                        <i className="fab fa-youtube"></i>
                                    </a>
                                    <a href={INSTAGRAM_URL} target="_blank" rel="noopener noreferrer" aria-label="Instagram">
                                        <i className="fab fa-instagram"></i>
                                    </a>
                                </div>
                            </div>
                        </div>

                        {/* Footer */}
                        <div className="wow-email-footer">
                            <p>&copy; {new Date().getFullYear()} Radha Madhav Society. Yugal Kunj, 2769 Duluth Hwy, Duluth, GA 30096</p>
                        </div>
                    </div>
                )}

                {/* List view */}
                {!loading && !selectedEmail && !loadingDetail && (
                    <>
                        {emails.length === 0 ? (
                            <div className="wow-empty">
                                <div className="wow-empty__icon">
                                    <i className="fas fa-envelope-open"></i>
                                </div>
                                <h5>No Words of Wisdom Available</h5>
                                <p>Check back soon for new "Words of Wisdom" messages from Didi Ji.</p>
                            </div>
                        ) : (
                            <>
                                <div className="wow-list-header">
                                    <div className="wow-list-header__hero">
                                        <img
                                            src={WOW_BANNER_URL}
                                            alt="Words of Wisdom"
                                            className="wow-list-header__hero-image"
                                            onError={handleImageError}
                                        />
                                    </div>
                                    <div className="wow-list-header__brand">
                                        <div>
                                            <div className="wow-list-header__org">Radha Madhav Society</div>
                                            <div className="wow-list-header__subtitle">Words of Wisdom</div>
                                        </div>
                                    </div>
                                    <p className="wow-list-header__desc">
                                        Inspirational messages from Didi Ji. Click any message to read the full reflection.
                                    </p>
                                </div>

                                <div className="wow-list-count">
                                    Showing {emails.length} message{emails.length !== 1 ? 's' : ''}
                                </div>

                                <div className="wow-list-grid">
                                    {emails.map((email) => (
                                        <div
                                            key={email.wordsOfWisdomId}
                                            className="wow-list-card"
                                            onClick={() => loadEmailDetail(email.wordsOfWisdomId)}
                                            role="button"
                                            tabIndex={0}
                                            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') loadEmailDetail(email.wordsOfWisdomId); }}
                                        >
                                            <div className="wow-list-card__top">
                                                <span className="wow-list-card__date">
                                                    <i className="far fa-calendar"></i> {formatDate(email.emailDate)}
                                                </span>
                                                <span className="wow-list-card__badge">
                                                    <i className="fas fa-om"></i>
                                                </span>
                                            </div>

                                            <h5 className="wow-list-card__subject">{email.subject}</h5>

                                            <p className="wow-list-card__from">
                                                <i className="fas fa-user-circle"></i>
                                                {email.senderName || 'Radha Madhav Society'}
                                            </p>

                                            <p className="wow-list-card__snippet">
                                                {email.snippet || 'Click to read this inspirational message.'}
                                            </p>

                                            <div className="wow-list-card__action">
                                                <span>Read More <i className="fas fa-arrow-right"></i></span>
                                            </div>
                                        </div>
                                    ))}
                                </div>

                                <div className="wow-list-footer">
                                    <p>Yours,</p>
                                    <p className="wow-list-footer__name">Didi Ji</p>
                                    <p className="wow-list-footer__note">
                                        Note: You may direct your spiritual questions to me directly at{' '}
                                        <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>
                                    </p>
                                </div>
                            </>
                        )}
                    </>
                )}

            </div>
        </div>
    );
};

export default WordofWisdomeCard;
